import { switchMap } from 'rxjs/operators';
import { showConfirmation } from '@modules/common';
import { ManagementFormAPI } from '@business/api/managementForm';
import { FormId } from '@business/entities/form';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { FolderId } from './../../entities/file/FolderResponse';
import {
  ManagementFormResponse,
  FormForwardUserStatus,
  ManagementFormID,
  FormForwardType,
  PostManagementFormCommentPayload,
  FetchManagementFormRequest,
} from '@business/entities/managementForms';
import { UserId } from '@business/entities/account';
import { ManagementFormCommentAPI } from '@business/api/management_form_comments';

export class ManagementFormBloc extends BaseBloc {
  private readonly itemsRepo = new Repository<ManagementFormResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.itemsRepo.items$;
  readonly selectForm = this.itemsRepo.selectItem;

  onReset() {
    this.itemsRepo.reset();
  }

  createForm = (name: string, folderId: FolderId, form: FormId, userId: UserId) => {
    return ManagementFormAPI.createForm(name, folderId, form, userId).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_form_file'),
      }),
    );
  };

  forwardForm = (id: FormId, users: UserId[], comment?: string, type?: FormForwardType) => {
    return ManagementFormAPI.forwardForm(id, users, comment, type).pipe(
      handleMessage({
        type: requestMessage('create_form_file'),
      }),
    );
  };

  fetchForms = (params: FetchManagementFormRequest) => {
    return ManagementFormAPI.fetchForms(params).pipe(this.itemsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_form_error') }));
  };

  fetchForm = (id: FormId) => {
    return ManagementFormAPI.fetchForm(id).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),

      handleMessage({ error: requestMessage('fetch_form_error') }),
    );
  };

  saveFormValues = (id: string, data: string, status: FormForwardUserStatus) => {
    return ManagementFormAPI.saveFormValues(id, data, status).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('save_form_values'),
      }),
    );
  };

  updateForwardedStatus = (id: string, status: FormForwardUserStatus, finalize?: boolean) => {
    return ManagementFormAPI.updateForwardedStatus(id, status, finalize).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('save_form_values'),
      }),
    );
  };

  createComment = (data: PostManagementFormCommentPayload) => {
    return ManagementFormCommentAPI.createComment(data).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_comment'),
      }),
    );
  };

  fetchComments = (formId: ManagementFormID) => {
    return ManagementFormCommentAPI.fetchComments(formId).pipe(handleMessage({ error: requestMessage('fetch_form_error') }));
  };

  updateFormName = (id: string, name: string) => {
    return ManagementFormAPI.updateFormName(id, name).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  toggleArchive = (id: string, archived: boolean) => {
    const title = archived ? 'Unarchive' : 'Archive';
    const description = archived ? 'Are you sure you want to unarchive this form?' : 'Are you sure you want to archive this form?';
    return showConfirmation(title, description, { btnYesLabel: title }).pipe(
      switchMap(() => {
        return ManagementFormAPI.toggleArchive(id);
      }),
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  toggleArchiveReceived = (id: FormId) => {
    return ManagementFormAPI.toggleArchiveReceived(id).pipe(
      handleMessage({
        type: requestMessage('archive_form'),
      }),
    );
  };
}
